import { createElement, useEffect, useRef, type CSSProperties, type ElementType, type ReactNode } from 'react'
import { registerReveal } from '../lib/reveal'

export type RevealVariant = 'up' | 'fade' | 'left' | 'right' | 'scale' | 'mask' | 'draw'

type RevealProps = {
  children?: ReactNode
  as?: ElementType
  variant?: RevealVariant
  /** Stagger position. Rendered as --i and multiplied by 70ms in CSS. */
  index?: number
  className?: string
  id?: string
  style?: CSSProperties
  role?: string
  'aria-label'?: string
  'aria-hidden'?: boolean
}

/**
 * Entrance animation wrapper. One observer per element, disconnected as soon as
 * the element has played, so a long page never accumulates scroll work.
 */
export function Reveal({
  children,
  as = 'div',
  variant = 'up',
  index = 0,
  className,
  style,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const node = ref.current
    return node ? registerReveal(node) : undefined
  }, [])

  return createElement(
    as,
    {
      ref,
      className,
      'data-reveal': variant,
      style: { ...style, ['--i' as string]: index },
      ...rest,
    },
    // The mask variant animates an inner element behind an overflow clip. The
    // observed element must stay unclipped: Chromium folds a target's own
    // clip-path into the intersection rect, so a self-clipped element never
    // reports as intersecting and would never reveal itself.
    variant === 'mask' ? createElement('span', { className: 'reveal-inner' }, children) : children,
  )
}
